import { Globe } from "lucide-react";
import { Button, Callout, Drawer, errorText } from "@amber/ui";
import { FindStep } from "./FindStep";
import { ManualStep } from "./ManualStep";
import { ProposalStep } from "./ProposalStep";
import { useCreateProfile, type CreateFlow } from "./useCreateProfile";

function StepHeader({ flow }: { flow: CreateFlow }) {
  const { step, proposal } = flow;
  return (
    <div className="mb-2 flex items-center gap-2 text-md text-muted">
      {step.kind === "proposal" ? <Globe size={13} aria-hidden="true" /> : null}
      <span>
        {step.kind === "proposal"
          ? `From ${proposal.data ? `${proposal.data.source.edition}.wikipedia.org` : "Wikipedia"}`
          : "Manual profile"}
      </span>
      <button
        type="button"
        className="font-strong text-link hover:underline"
        onClick={() => {
          flow.setStep({ kind: "find" });
          flow.setCompleting(null);
        }}
      >
        Other candidates
      </button>
    </div>
  );
}

export function CreateProfile({ onClose }: { onClose: () => void }) {
  const flow = useCreateProfile();
  const { step, completing, error } = flow;
  return (
    <Drawer
      title="New profile"
      subtitle="Creates a draft. Source discovery then drafts a first plan; nothing is monitored until tracking starts."
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">
            {completing ? (
              "Nothing changes until you confirm."
            ) : (
              <>
                Saved as <b className="font-strong text-fg">Draft</b>.
              </>
            )}
          </span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="create-profile-form"
            disabledReason={flow.blocked}
            aria-busy={flow.busy || undefined}
          >
            {completing ? "Add identity" : "Create draft"}
          </Button>
        </span>
      }
    >
      <form
        id="create-profile-form"
        noValidate
        className="text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          flow.submit();
        }}
      >
        {step.kind === "find" ? <FindStep flow={flow} /> : <StepHeader flow={flow} />}
        {step.kind === "proposal" ? <ProposalStep flow={flow} /> : null}
        {step.kind === "manual" ? <ManualStep flow={flow} /> : null}
        {error ? (
          <Callout tone="attention" className="mt-3" role="alert">
            {errorText(error)}
          </Callout>
        ) : null}
      </form>
    </Drawer>
  );
}
